/** Static configuration tables for {@link SidebarRail}. */

/**
 * Single-pane rail keys that open directly (no expandable module group) — they
 * never show a count badge or chevron.
 */
export const LEAF_GROUP_RAIL_KEYS = new Set(['files', 'places', 'pages', 'health', 'synthesis']);

/** Maps rail item keys → module types they match. */
export const RAIL_MODULE_TYPES: Record<string, string[]> = {
  email: ['email'],
  calendar: ['event_list'],
  tasks: ['task_list'],
  contacts: ['contact_list'],
  clients: ['client_list'],
  files: ['drive', 'file_viewer', 'docs', 'note_grid', 'photos', 'gallery'],
  places: ['places', 'place_list'],
  // The Stats rail item was removed on both desktop and mobile (#5272); this
  // entry stays so stat modules remain classified as core (ALL_CORE_MODULE_TYPES)
  // instead of surfacing in the custom-modules list.
  stats: ['stat_list', 'stat_chart'],
  pages: ['pages', 'report', 'html_viewer'],
  notes: ['notes'],
  health: ['health'],
  channels: ['channels'],
  dms: ['channels'],
  // 'agents' kept alongside the canonical 'automations' so already-persisted
  // module rows (type 'agents') still group under — and keep visible — the
  // renamed Automations rail. New modules are created as 'automations'.
  automations: ['automations', 'agents'],
  synthesis: ['synthesis'],
};

/** Default sidebar visibility — true = shown by default. */
export const DEFAULT_VISIBILITY: Record<string, boolean> = {
  email: true,
  calendar: true,
  drive: true,
  tasks: true,
  contacts: true,
  clients: false,
  places: true,
  pages: true,
  notes: true,
  health: false,
  news: true,
  channels: false,
  dms: false,
  shopify: false,
  mixpanel: false,
  automations: true,
  synthesis: true,
};

/** Module type to create when a checklist item is checked and no module exists. */
export const CHECKLIST_MODULE_TYPES: Record<string, { type: string; label: string }> = {
  email: { type: 'gmail', label: 'Email' },
  calendar: { type: 'event_list', label: 'Calendar' },
  drive: { type: 'drive', label: 'Drive' },
  tasks: { type: 'task_list', label: 'Kanban' },
  contacts: { type: 'contact_list', label: 'Contacts' },
  clients: { type: 'client_list', label: 'Clients' },
  places: { type: 'places', label: 'Places' },
  pages: { type: 'pages', label: 'Studio' },
  notes: { type: 'notes', label: 'Notes' },
  health: { type: 'health', label: 'Health' },
  news: { type: 'news', label: 'News' },
  channels: { type: 'channels', label: 'Channels' },
  shopify: { type: 'shopify-crm', label: 'Shopify CRM' },
  mixpanel: { type: 'mixpanel', label: 'Mixpanel' },
  automations: { type: 'automations', label: 'Automations' },
  synthesis: { type: 'synthesis', label: 'Synthesis' },
};

/** All module types claimed by a core rail key. */
export const ALL_CORE_MODULE_TYPES = new Set(Object.values(RAIL_MODULE_TYPES).flat());

/** Flyout display titles for TEAM workspaces. */
export const RAIL_LABELS: Record<string, string> = {
  email: 'EMAIL',
  calendar: 'TEAM CALENDAR',
  tasks: 'TEAM KANBAN',
  contacts: 'TEAM CONTACTS',
  files: 'TEAM DRIVE',
  places: 'PLACES',
  pages: 'STUDIO',
  notes: 'NOTES',
  health: 'HEALTH',
  automations: 'AUTOMATIONS',
  synthesis: 'SYNTHESIS',
};

/** Flyout display titles for personal (solo) workspaces — no "Team" prefix. */
export const PERSONAL_RAIL_LABELS: Record<string, string> = {
  email: 'EMAIL',
  calendar: 'CALENDAR',
  tasks: 'KANBAN',
  contacts: 'CONTACTS',
  files: 'DRIVE',
  places: 'PLACES',
  pages: 'STUDIO',
  notes: 'NOTES',
};

/** Rail items backed by plugins instead of modules. */
export const RAIL_PLUGIN_IDS: Record<string, string> = {
};

/** Maps rail keys to checklist/visibility keys where they differ (files → drive). */
export const railKeyToVisibilityKey: Record<string, string> = { files: 'drive' };

/**
 * Rail keys for the PRIVATE / provider-backed modules (per-member data: mail,
 * calendar, drive). In a TEAM workspace these stay in the top "MODULES" group;
 * everything else moves under a "TEAM MODULES" header (shared Xavior-native data).
 * Note: Drive's rail key is `files`.
 */
export const PROVIDER_RAIL_KEYS = new Set(['email', 'calendar', 'files']);

// NOTE (ADR 042 §13, Phase 7): the former `TEAM_ONLY_RAIL_KEYS` (channels, dms,
// agents, synthesis, health) and `PERSONAL_ONLY_RAIL_KEYS` (notes) hardcoded
// gating sets were RETIRED into the entitlement system. Rail-key visibility now
// derives from the per-workspace grants (`channels` is team-baseline only;
// notes/agents/synthesis/health are ungranted at launch) via `isRailKeyHidden`
// in SidebarRail.tsx — see the entitlement baselines in @xavior/domain.

/**
 * Rail keys for the SHARED section in a team workspace — shown under the
 * SHARED header, toggled by the bottom sliders. Everything else is personal.
 */
export const SHARED_RAIL_KEYS = new Set(['channels', 'dms', 'tasks', 'contacts', 'calendar', 'files', 'pages']);


/**
 * Default module-rail order for a workspace with no custom (persisted) order.
 * AI entry points (Automations, Synthesis) lead, then provider modules (Email,
 * Calendar, Drive), then the native data modules. In a team workspace the
 * provider trio is pulled into the top "MODULES" group, so Automations/Synthesis
 * lead the "TEAM MODULES" group. Note: Drive's rail key is `files`.
 */
export const DEFAULT_RAIL_ORDER: readonly string[] = [
  'email', 'calendar', 'channels', 'dms', 'tasks', 'contacts', 'files', 'pages', 'automations', 'notes', 'places',
];

/** Sort order for the TEAM section in a team workspace (Channels/DMs lead). */
export const TEAM_RAIL_ORDER: readonly string[] = [
  'channels', 'dms', 'tasks', 'contacts', 'calendar', 'files', 'pages', 'notes', 'places',
];

/** Module types that should only exist once per workspace (like Drive). */
export const SINGLETON_MODULE_TYPES = new Set(['drive', 'places', 'pages', 'notes', 'gmail', 'event_list', 'sidebar_settings']);
